// CHANGE: Removed all debug console statements for production
// TaskEditModal: Modifications structurelles des missions (opérateur, date, heure)
// Recentré sur les modifications structurelles uniquement — statut et champs de saisie
// sont désormais gérés directement depuis TaskDetailModal (hub d'actions rapides)
// FEATURES:
// - Statut affiché en lecture seule dans le header (non modifiable ici)
// - Modification de l'opérateur assigné
// - Modification de la date et de l'heure
// - Scope selection pour les missions récurrentes (single, all_future, until_date)
// - Change detection with disabled submit when no changes
// - Scope selection always visible for recurring tasks
// - Summary panel showing all modifications before confirmation
// UPDATED: Suppression du dropdown de statut et de la section champs de saisie
// UPDATED: Typography harmonization - section headings in overline style

import { useState, useEffect } from 'react';
import { X, Clock, AlertCircle, CalendarCheck, CalendarRange, CalendarClock, Check, Info } from 'lucide-react';
import type { AssignedTaskWithDetails } from '../../lib/execution';
import { formatDate, formatTimeToHHMM } from '../../lib/dateUtils';
import { getFullName } from '../../lib/supabase';
import { TimePicker } from '../ui/TimePicker';
import { DatePicker } from '../ui/DatePicker';
import { getRecurrence, getRecurrencePattern } from '../../lib/recurrence';
import type { RecurrenceRule } from '../../lib/supabase';
import { SearchableSelect } from '../ui/SearchableSelect';
import { FormSection } from '../ui/FormSection';
import { FormField } from '../ui/FormField';
import { TYPOGRAPHY } from '../../lib/designSystem';

interface Operator {
  id: string;
  first_name: string;
  last_name: string;
  username: string;
}

interface TaskEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: AssignedTaskWithDetails | null;
  operators: Operator[];
  onReassign: (newUserId: string | null, scope: 'single' | 'all_future' | 'until_date', untilDate?: string, newScheduledTime?: string, newScheduledDate?: string) => Promise<void>;
  initialOperatorId?: string;
  initialScheduledTime?: string;
  onSaveComplete: () => void;
}

export function TaskEditModal({
  isOpen,
  onClose,
  task,
  operators,
  onReassign,
  initialOperatorId,
  initialScheduledTime,
  onSaveComplete
}: TaskEditModalProps) {
  const [selectedOperatorId, setSelectedOperatorId] = useState<string>('');
  const [newScheduledDate, setNewScheduledDate] = useState<string>('');
  const [newScheduledTime, setNewScheduledTime] = useState<string>('');
  const [scope, setScope] = useState<'single' | 'all_future' | 'until_date'>('single');
  const [untilDate, setUntilDate] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule | null>(null);
  const [loadingRecurrence, setLoadingRecurrence] = useState(false);

  // Reset state when modal opens/closes or task changes
  useEffect(() => {
    if (isOpen && task) {
      setSelectedOperatorId(initialOperatorId || task.assigned_to_user_id || '');
      setNewScheduledDate(task.assigned_date || '');
      setNewScheduledTime(initialScheduledTime || task.scheduled_time || '');
      setScope('single');
      setUntilDate('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, task, initialOperatorId, initialScheduledTime]);

  // Load recurrence rule if task is recurring
  useEffect(() => {
    if (!task || !task.is_recurring || !task.template_id) {
      setRecurrenceRule(null);
      return;
    }

    let isMounted = true;

    const loadRecurrence = async () => {
      setLoadingRecurrence(true);
      try {
        const rule = await getRecurrence(task.template_id);
        if (isMounted) setRecurrenceRule(rule);
      } catch {
      } finally {
        if (isMounted) setLoadingRecurrence(false);
      }
    };

    loadRecurrence();
    return () => { isMounted = false; };
  }, [task?.id, task?.is_recurring, task?.template_id]);

  const minUntilDate = task?.assigned_date
    ? formatDate(new Date(new Date(task.assigned_date).getTime() + 86400000))
    : formatDate(new Date(Date.now() + 86400000));

  if (!isOpen || !task) return null;

  const operatorChanged = selectedOperatorId !== task.assigned_to_user_id;
  const dateChanged = newScheduledDate !== task.assigned_date;
  const timeChanged = newScheduledTime !== task.scheduled_time;
  const hasChanges = operatorChanged || dateChanged || timeChanged;

  const getStatusConfig = (status: 'not_started' | 'completed' | 'overdue') => {
    switch (status) {
      case 'completed':
        return { label: 'Terminée', icon: Check, bgColor: 'bg-emerald-50', textColor: 'text-emerald-700' };
      case 'overdue':
        return { label: 'En retard', icon: AlertCircle, bgColor: 'bg-red-50', textColor: 'text-red-700' };
      default:
        return { label: 'Non démarrée', icon: Clock, bgColor: 'bg-amber-50', textColor: 'text-amber-700' };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!hasChanges) {
      setError('Aucune modification détectée');
      return;
    }

    if (scope === 'until_date' && !untilDate) {
      setError('Veuillez sélectionner une date de fin');
      return;
    }

    if (scope === 'until_date' && untilDate && task?.assigned_date && untilDate <= task.assigned_date) {
      setError('La date de fin doit être postérieure à la date de l\'occurrence');
      return;
    }

    setIsSubmitting(true);

    try {
      await onReassign(
        operatorChanged ? selectedOperatorId : null,
        scope,
        scope === 'until_date' ? untilDate : undefined,
        timeChanged ? newScheduledTime : undefined,
        dateChanged ? newScheduledDate : undefined
      );

      onSaveComplete();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la modification');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentOperator = operators.find(op => op.id === selectedOperatorId);

  const formatDateShort = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const statusConfig = getStatusConfig(task.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-gray-100">
          {/* Close button */}
          <div className="flex items-center justify-end mb-3">
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isSubmitting}
              title="Fermer"
            >
              <X size={18} />
            </button>
          </div>

          {/* Title */}
          <h2 className={`${TYPOGRAPHY.modalTitle} mb-2 leading-tight`}>
            {task.template.name}
          </h2>

          {/* Badges: read-only status + recurrence */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 ${statusConfig.bgColor} ${statusConfig.textColor} text-xs font-medium rounded`}>
              <StatusIcon size={12} />
              {statusConfig.label}
            </span>
            {task.is_recurring && recurrenceRule && getRecurrencePattern(recurrenceRule) && (
              <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded">
                {getRecurrencePattern(recurrenceRule)}
              </span>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          <form onSubmit={handleSubmit}>
            {/* Section: Planning */}
            <FormSection title="Planification">
              <div className="space-y-4">
                <FormField label="Opérateur">
                  <SearchableSelect
                    value={selectedOperatorId}
                    onChange={setSelectedOperatorId}
                    options={operators.map(op => ({
                      value: op.id,
                      label: getFullName(op)
                    }))}
                    disabled={isSubmitting}
                    placeholder="Sélectionner un opérateur"
                    searchPlaceholder="Rechercher un opérateur..."
                  />
                </FormField>

                <FormField label="Date">
                  <DatePicker
                    value={newScheduledDate}
                    onChange={setNewScheduledDate}
                    disabled={isSubmitting}
                    className="w-full"
                  />
                </FormField>

                <FormField label="Heure">
                  <TimePicker
                    value={newScheduledTime}
                    onChange={setNewScheduledTime}
                    step={15}
                    disabled={isSubmitting}
                  />
                </FormField>
              </div>
            </FormSection>

            {/* Section: Scope (recurring tasks only) */}
            {task.is_recurring && (
              <FormSection title="Appliquer à" className="mb-4">
                <div className="space-y-2">
                  <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    scope === 'single' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}>
                    <input
                      type="radio"
                      name="scope"
                      value="single"
                      checked={scope === 'single'}
                      onChange={(e) => setScope(e.target.value as 'single')}
                      disabled={isSubmitting}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CalendarCheck size={16} className={scope === 'single' ? 'text-primary-600' : 'text-gray-400'} />
                        <span className={`${TYPOGRAPHY.bodyRegular} font-medium ${scope === 'single' ? 'text-primary-900' : 'text-gray-900'}`}>
                          Uniquement ce jour
                        </span>
                      </div>
                      <span className={TYPOGRAPHY.helperText}>{formatDateShort(task.assigned_date)}</span>
                    </div>
                  </label>

                  <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    scope === 'all_future' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}>
                    <input
                      type="radio"
                      name="scope"
                      value="all_future"
                      checked={scope === 'all_future'}
                      onChange={(e) => setScope(e.target.value as 'all_future')}
                      disabled={isSubmitting}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CalendarRange size={16} className={scope === 'all_future' ? 'text-primary-600' : 'text-gray-400'} />
                        <span className={`${TYPOGRAPHY.bodyRegular} font-medium ${scope === 'all_future' ? 'text-primary-900' : 'text-gray-900'}`}>
                          Ce jour et tous les suivants
                        </span>
                      </div>
                      <span className={TYPOGRAPHY.helperText}>À partir du {formatDateShort(task.assigned_date)}</span>
                    </div>
                  </label>

                  <label className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    scope === 'until_date' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}>
                    <input
                      type="radio"
                      name="scope"
                      value="until_date"
                      checked={scope === 'until_date'}
                      onChange={(e) => setScope(e.target.value as 'until_date')}
                      className="mt-0.5"
                      disabled={isSubmitting}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <CalendarClock size={16} className={scope === 'until_date' ? 'text-primary-600' : 'text-gray-400'} />
                        <span className={`${TYPOGRAPHY.bodyRegular} font-medium ${scope === 'until_date' ? 'text-primary-900' : 'text-gray-900'}`}>
                          Jusqu'à une date
                        </span>
                      </div>
                      {scope === 'until_date' && (
                        <DatePicker
                          value={untilDate}
                          onChange={setUntilDate}
                          minDate={minUntilDate}
                          disabled={isSubmitting}
                          required
                          className="mt-2"
                        />
                      )}
                    </div>
                  </label>
                </div>
              </FormSection>
            )}

            {/* Summary of changes */}
            {hasChanges && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 border-t-2 pt-5 mt-5">
                <h4 className={`${TYPOGRAPHY.bodyRegular} font-medium text-blue-900 mb-2 flex items-center gap-2`}>
                  <Info size={16} />
                  Résumé des modifications
                </h4>
                <div className={`space-y-1 ${TYPOGRAPHY.bodyRegular} text-blue-700`}>
                  {operatorChanged && (
                    <div>
                      <span className="font-medium">Opérateur :</span>{' '}
                      {operators.find(op => op.id === task.assigned_to_user_id)
                        ? getFullName(operators.find(op => op.id === task.assigned_to_user_id)!)
                        : 'Non assigné'}{' '}
                      → {currentOperator ? getFullName(currentOperator) : 'Non assigné'}
                    </div>
                  )}
                  {dateChanged && (
                    <div>
                      <span className="font-medium">Date :</span>{' '}
                      {formatDateShort(task.assigned_date)} → {formatDateShort(newScheduledDate)}
                    </div>
                  )}
                  {timeChanged && (
                    <div>
                      <span className="font-medium">Heure :</span>{' '}
                      {formatTimeToHHMM(task.scheduled_time, 'Non définie')} → {formatTimeToHHMM(newScheduledTime, 'Non définie')}
                    </div>
                  )}
                  {task.is_recurring && (
                    <div>
                      <span className="font-medium">Appliqué à :</span>{' '}
                      {scope === 'single' && `Uniquement le ${formatDateShort(task.assigned_date)}`}
                      {scope === 'all_future' && 'Ce jour et tous les suivants'}
                      {scope === 'until_date' && untilDate && `Du ${formatDateShort(task.assigned_date)} au ${formatDateShort(untilDate)}`}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mb-4 mt-4 flex items-start gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span className={TYPOGRAPHY.bodyRegular}>{error}</span>
              </div>
            )}

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className={`px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors ${TYPOGRAPHY.bodyRegular}`}
                disabled={isSubmitting}
              >
                Annuler
              </button>
              <button
                type="submit"
                className={`px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed ${TYPOGRAPHY.bodyRegular} font-medium`}
                disabled={isSubmitting || !hasChanges}
              >
                {isSubmitting ? 'En cours...' : 'Confirmer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
